import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, ChevronDown, ChevronRight, Edit, Plus, RefreshCw, X } from 'lucide-react';
import MainService from '../../services/MainService';
import { ACCOUNT_NATURE_OPTIONS, parseApiList } from '../../utils/accountingHelpers';

const panelClass = 'rounded-xl border border-gray-200 bg-white shadow-sm';
const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

function NatureRadios({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {ACCOUNT_NATURE_OPTIONS.map((opt) => (
        <label key={opt.value} className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50">
          <input type="radio" name="nature" value={opt.value} checked={value === opt.value} onChange={() => onChange(opt.value)} />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function TypeTreeNode({ node, onAddChild, onEdit, onLoadChildren, expandedIds, onToggle }) {
  const hasChildren = node.Children && node.Children.length > 0;
  const isExpanded = expandedIds.has(node.Id);
  const isParent = node.Level === 1;

  return (
    <li className="ml-4 border-l border-gray-200 pl-3">
      <div className="flex items-center gap-2 py-1.5">
        {!isParent ? (
          <span className="inline-flex h-5 w-5 items-center justify-center text-gray-400">•</span>
        ) : (
          <button
            type="button"
            className="inline-flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-100"
            onClick={() => onToggle(node)}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        )}
        <span className="flex-1 text-sm font-medium text-gray-800">{node.Name}</span>
        {node.Code ? <span className="text-xs text-gray-500">({node.Code})</span> : null}
        {isParent ? (
          <button type="button" className="rounded p-1 text-primary hover:bg-primary/10" title="Add child type" onClick={() => onAddChild(node)}>
            <Plus className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" className="rounded p-1 text-gray-600 hover:bg-gray-100" title="Edit" onClick={() => onEdit(node)}>
            <Edit className="h-4 w-4" />
          </button>
        )}
      </div>
      {isParent && isExpanded ? (
        <ul className="space-y-1">
          {(node.Children || []).map((child) => (
            <TypeTreeNode
              key={child.Id}
              node={child}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onLoadChildren={onLoadChildren}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
          {!hasChildren ? <li className="ml-6 py-1 text-xs text-gray-400">No child types</li> : null}
        </ul>
      ) : null}
    </li>
  );
}

const AccountType = () => {
  const [roots, setRoots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ Id: 0, Name: '', Code: '', Level: 1, ParentId: null, IsActive: true });
  const [parentName, setParentName] = useState('');

  const loadRoots = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setRoots(parseApiList(await MainService.AccountTypeParentList()));
    } catch {
      setError('Failed to load account types');
      setRoots([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoots();
  }, [loadRoots]);

  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const loadChildren = async (node) => {
    if (node.Children) return;
    try {
      const children = parseApiList(await MainService.AccountTypeSubList(node.Id));
      setRoots((prev) => {
        const attach = (list) =>
          list.map((item) => {
            if (item.Id === node.Id) return { ...item, Children: children };
            if (item.Children) return { ...item, Children: attach(item.Children) };
            return item;
          });
        return attach(prev);
      });
    } catch {
      setError('Failed to load child account types');
    }
  };

  const toggleExpand = async (node) => {
    const next = new Set(expandedIds);
    if (next.has(node.Id)) {
      next.delete(node.Id);
    } else {
      next.add(node.Id);
      await loadChildren(node);
    }
    setExpandedIds(next);
  };

  const openAdd = (parent) => {
    setForm({ Id: 0, Name: '', Code: 'G', Level: 2, ParentId: parent.Id, IsActive: true });
    setParentName(parent.Name);
    setModal('add');
  };

  const openEdit = (node) => {
    setForm({
      Id: node.Id,
      Name: node.Name || '',
      Code: node.Code || 'G',
      Level: node.Level || 2,
      ParentId: node.ParentId || null,
      IsActive: node.IsActive !== false,
    });
    setParentName('');
    setModal('edit');
  };

  const closeModal = () => {
    setModal(null);
    setParentName('');
  };

  const save = async () => {
    if (!form.Name.trim()) {
      setError('Account type name is required');
      return;
    }
    if (form.Level !== 1 && !form.Code) {
      setError('Select account nature');
      return;
    }
    try {
      setLoading(true);
      const api = form.Id > 0 ? MainService.UpdateAccountType : MainService.AddAccountType;
      const result = await api(form);
      if (Number(result) === 1 || result === true || result === 'Success') {
        setSuccess('Account type saved');
        closeModal();
        setExpandedIds(new Set());
        await loadRoots();
      } else {
        setError('Failed to save account type');
      }
    } catch {
      setError('Failed to save account type');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Types</h1>
          <p className="text-sm text-gray-500">Define account classification groups used in the chart of accounts.</p>
        </div>
        <button
          type="button"
          onClick={loadRoots}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {success}
        </div>
      ) : null}

      <div className={`${panelClass} p-4`}>
        {loading && !roots.length ? (
          <p className="py-8 text-center text-sm text-gray-500">Loading account types…</p>
        ) : roots.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No account types found.</p>
        ) : (
          <ul className="space-y-2">
            {roots.map((node) => (
              <TypeTreeNode
                key={node.Id}
                node={node}
                onAddChild={openAdd}
                onEdit={openEdit}
                onLoadChildren={loadChildren}
                expandedIds={expandedIds}
                onToggle={toggleExpand}
              />
            ))}
          </ul>
        )}
      </div>

      {modal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {modal === 'add' ? 'Add Account Type' : 'Edit Account Type'}
              </h2>
              <button type="button" onClick={closeModal} className="rounded p-1 text-gray-500 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>
            {parentName ? <p className="mb-3 text-sm text-gray-600">Under group: <strong>{parentName}</strong></p> : null}
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
                <input className={inputClass} value={form.Name} onChange={(e) => setForm((p) => ({ ...p, Name: e.target.value }))} />
              </div>
              {form.Level !== 1 ? (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Nature</label>
                  <NatureRadios value={form.Code} onChange={(code) => setForm((p) => ({ ...p, Code: code }))} />
                </div>
              ) : null}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button type="button" onClick={save} disabled={loading} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AccountType;
